/**
 * AssetPickerUploadTab - Drag & Drop / File picker for uploading reference images
 *
 * File is kept in browser memory until user confirms.
 * Validation: max 16MB, PNG/JPG/WebP, min 512x512px.
 */

import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, AlertTriangle } from 'lucide-react';
import { Button, Alert, AlertDescription } from '@/components/v2/ui';
import { useUploadValidation, type ValidatedFile } from './useUploadValidation';

const ACCEPT_STRING = 'image/png,image/jpeg,image/webp';

interface AssetPickerUploadTabProps {
  onSelect: (file: File) => void;
}

export function AssetPickerUploadTab({ onSelect }: AssetPickerUploadTabProps) {
  const { t } = useTranslation();
  const { validated, error, isValidating, validateAndSet, clearFile } =
    useUploadValidation();
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (files?.[0]) validateAndSet(files[0]);
    },
    [validateAndSet],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  if (validated) {
    return (
      <UploadPreview
        validated={validated}
        onClear={clearFile}
        onConfirm={() => onSelect(validated.file)}
      />
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <Alert variant="warning" icon={<AlertTriangle className="h-4 w-4" />}>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div
        role="button"
        tabIndex={0}
        aria-label={t('common.imageUploadHint')}
        className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 cursor-pointer transition-colors ${
          isDragging
            ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
            : 'border-neutral-300 dark:border-neutral-700 hover:border-primary-300'
        }`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <Upload className="h-8 w-8 text-neutral-400" />
        <p className="text-sm text-neutral-600 dark:text-neutral-400 text-center">
          {isValidating ? 'Bild wird geprueft...' : 'Bild hierher ziehen oder klicken'}
        </p>
        <p className="text-xs text-neutral-400">
          PNG, JPG, WebP &middot; max 16 MB &middot; min 512&times;512 px
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_STRING}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Preview sub-component
// ============================================================================

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface UploadPreviewProps {
  validated: ValidatedFile;
  onClear: () => void;
  onConfirm: () => void;
}

function UploadPreview({ validated, onClear, onConfirm }: UploadPreviewProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 py-4">
      <div className="relative">
        <img
          src={validated.previewUrl}
          alt={t('common.preview')}
          className="h-48 w-48 rounded-lg object-cover border border-neutral-200 dark:border-neutral-700"
        />
        <Button
          variant="outline"
          size="icon"
          className="absolute -top-2 -right-2 h-7 w-7 rounded-full"
          onClick={onClear}
          aria-label="Bild entfernen"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="text-center text-sm text-neutral-600 dark:text-neutral-400 space-y-0.5">
        <p className="font-medium truncate max-w-[260px]">{validated.file.name}</p>
        <p>{validated.width} &times; {validated.height} px &middot; {formatSize(validated.file.size)}</p>
      </div>
      <Button variant="primary" onClick={onConfirm}>
        Uebernehmen
      </Button>
    </div>
  );
}
